// @flow
import { PDFFillerContext } from "pages/Contexts/PdfFillerContext";
import PdfPage from "pages/PDFFiller/Components/PdfPage";
import ZoomControl from "pages/PDFFiller/Components/ZoomControl";
import * as React from "react";
import styled from "styled-components";
import FormContainer from "ui/atoms/FormContainer";
import Space from "ui/atoms/Space";
import PageTitle from "ui/molecules/PageTitle";
import PageTitleInput from "ui/molecules/PageTitleInput";

import useSearchParams from "@/hooks/useSearchParams";
import history from "@/utils/history";
import cn from "@/utils/tailwind/cn";
const ZoomWrapper = styled.div`
  display: inline-block;
  width: 300px;
  margin-bottom: 10px;
`;

const PagesContainer = styled(Space)`
  padding-bottom: 80px;
  overflow-x: scroll;
`;

function DocumentContainer(): React.Node {
  const searchParams = useSearchParams();
  const {
    documentName,
    error,
    pages,
    pagesContainerRef,
    setDocumentName,
    setError,
    viewMode,
    setSettingsOpen,
    setSelectedInputFieldId,
  } = React.useContext(PDFFillerContext);

  const [nameIsActive, setNameIsActive] = React.useState(false);
  const [nameBlur, setNameBlur] = React.useState(false);

  React.useEffect(() => {
    if (nameBlur && !Boolean(documentName) && !error) {
      setError("Name is required");
    }
    if (nameBlur && Boolean(documentName) && error) {
      setError("");
    }
  }, [documentName, error, nameBlur, setError]);

  const activateName = React.useCallback(() => {
    setNameIsActive(true);
    setSettingsOpen(true);
    setSelectedInputFieldId(null);
  }, [setSettingsOpen, setSelectedInputFieldId]);

  const deactivateName = React.useCallback(() => {
    setNameIsActive(false);
  }, []);

  const handleChange = React.useCallback(
    (event) => {
      setDocumentName(event.currentTarget.value);
    },
    [setDocumentName],
  );

  const handleBlur = React.useCallback(() => {
    deactivateName();
    if (!nameBlur) {
      setNameBlur(true);
    }
  }, [deactivateName, nameBlur]);

  const isTabletMode = history?.location?.pathname?.includes("/t/p/");
  const isClientTabletMode = history?.location?.pathname?.includes("/client");
  const isClientResponse = history?.location?.pathname?.includes("/r/");
  const isPacketOfficeUse = history?.location?.pathname?.includes(
    "/fillPacketOfficeUseQuestions/",
  );
  const isPacket = searchParams.get("response_type") === "packet";

  const innerContainerStyles = {
    "!pb-[140px]": isTabletMode && isClientTabletMode,
    "!pb-[150px]": isPacket || isClientResponse,
    "!pb-10": isPacketOfficeUse,
  };

  if (!(pages.length > 0)) return null;

  return (
    <FormContainer
      containerClassName={cn(innerContainerStyles)}
      noPaddingTop={viewMode === "response"}
      style={{ overflow: "visible" }}
    >
      {!["edit", "response"].includes(viewMode) && (
        <PageTitle center text={documentName} />
      )}

      {!["edit", "response"].includes(viewMode) && (
        <ZoomWrapper>
          <ZoomControl />
        </ZoomWrapper>
      )}
      <Space hideOverflow style={{ flex: 1 }}>
        {viewMode === "edit" && (
          <PageTitleInput
            active={nameIsActive}
            error={error}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={activateName}
            placeholder="Document Name"
            value={documentName || ""}
          />
        )}
        <PagesContainer ref={pagesContainerRef}>
          {pages.map((page) => (
            <PdfPage key={page.id} page={page} />
          ))}
        </PagesContainer>
      </Space>
    </FormContainer>
  );
}

export default DocumentContainer;
